// ─── Client du canal « fichiers » (protocole Telipso Assistance v2 §2) ───
// Requêtes corrélées par `id`, morceaux binaires de 64 Kio (0x10 poste ▸ console,
// 0x11 console ▸ poste). Partagé par le panneau Fichiers et par le presse-papiers
// partagé de l'écran, qui ouvrent chacun leur propre canal.

export const CHUNK = 64 * 1024;
export const FRAME_DOWNLOAD = 0x10;
export const FRAME_UPLOAD = 0x11;
export const MAX_BUFFERED = 4 * 1024 * 1024;

/** Assemble un chemin du poste (Windows ou Unix) à partir d'un dossier et d'un nom. */
export function joindre(dossier, nom) {
  const sep = dossier.includes('\\') || /^[A-Za-z]:/.test(dossier) ? '\\' : '/';
  return dossier.endsWith(sep) ? dossier + nom : dossier + sep + nom;
}

export function fmtTaille(n) {
  if (n == null) return '';
  if (n < 1024) return n + ' o';
  const u = ['Kio', 'Mio', 'Gio', 'Tio'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return v.toFixed(v < 10 ? 1 : 0) + ' ' + u[i];
}

/** Livre un fichier assemblé au navigateur (<a download>). */
export function enregistrer(nom, morceaux) {
  const url = URL.createObjectURL(new Blob(morceaux));
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/**
 * @param {() => {envoyer: Function, envoyerBinaire: Function, enAttente: Function, etat: string}} canal
 *        accesseur du canal courant (celui que rend useChannel, à jour à chaque rendu).
 *
 * Observateur (tous les rappels sont facultatifs) :
 *   debut(id, nom, total, sens)  sens = 'up' | 'down'
 *   progres(id, fait)
 *   fini(id, total)
 *   erreur(id, message)
 *   annule()  ▸ true interrompt un téléversement (entre deux morceaux)
 */
export class ClientFichiers {
  constructor(canal) {
    this.canal = canal;
    this.prochainId = 1;
    this.attentes = new Map();
    this.telechargements = new Map();
  }

  /** Requête / réponse corrélée par id ; la réponse (ou l'erreur du poste) clôt la promesse. */
  demander(message) {
    return this.requete(this.prochainId++, message);
  }

  requete(id, message) {
    return new Promise((resoudre, rejeter) => {
      this.attentes.set(id, { resoudre, rejeter });
      this.canal().envoyer({ ...message, id });
    });
  }

  /** À appeler à la fermeture du canal : les requêtes en attente ne reviendront pas. */
  rejeterTout(raison) {
    this.attentes.forEach((a) => a.rejeter(new Error(raison)));
    this.attentes.clear();
  }

  /** Message reçu du canal (texte ou binaire). */
  traiter(e) {
    if (typeof e.data !== 'string') {
      const v = new DataView(e.data);
      if (v.byteLength < 5 || v.getUint8(0) !== FRAME_DOWNLOAD) return;
      const id = v.getUint32(1);
      const t = this.telechargements.get(id);
      if (!t) return;
      const morceau = e.data.slice(5);
      t.morceaux.push(morceau);
      t.recu += morceau.byteLength;
      t.obs.progres?.(id, t.recu);
      return;
    }
    let m;
    try { m = JSON.parse(e.data); } catch { return; }
    if (m.t === 'debut_telechargement') {
      const t = this.telechargements.get(m.id);
      if (t) {
        t.nom = m.nom;
        t.obs.debut?.(m.id, m.nom, m.taille, 'down');
      }
      return;
    }
    if (m.t === 'fin_telechargement') {
      const t = this.telechargements.get(m.id);
      this.telechargements.delete(m.id);
      if (t) {
        t.obs.fini?.(m.id, t.recu);
        t.resoudre({ nom: t.nom, morceaux: t.morceaux });
      }
      return;
    }
    if (m.t === 'erreur' && this.telechargements.has(m.id)) {
      const t = this.telechargements.get(m.id);
      this.telechargements.delete(m.id);
      t.obs.erreur?.(m.id, m.message);
      t.rejeter(new Error(m.message || 'Erreur'));
      return;
    }
    const a = this.attentes.get(m.id);
    if (!a) return;
    this.attentes.delete(m.id);
    if (m.t === 'erreur') a.rejeter(new Error(m.message || 'Erreur'));
    else a.resoudre(m);
  }

  /** Télécharge un fichier du poste : résout avec { nom, morceaux } (à passer à enregistrer). */
  telecharger(chemin, obs = {}) {
    const id = this.prochainId++;
    return new Promise((resoudre, rejeter) => {
      this.telechargements.set(id, { nom: null, morceaux: [], recu: 0, obs, resoudre, rejeter });
      this.canal().envoyer({ t: 'telecharger', id, chemin });
    });
  }

  /** Téléverse un File dans un dossier du poste, avec contre-pression sur le tampon du WebSocket. */
  async televerser(fichier, dossier, obs = {}) {
    const id = this.prochainId++;
    obs.debut?.(id, fichier.name, fichier.size, 'up');
    try {
      await this.requete(id, { t: 'televerser', dossier, nom: fichier.name, taille: fichier.size });
      for (let pos = 0; pos < fichier.size; pos += CHUNK) {
        while (this.canal().enAttente() > MAX_BUFFERED) await new Promise((r) => setTimeout(r, 50));
        if (this.canal().etat === 'ferme') throw new Error('Canal fermé');
        if (obs.annule?.()) throw new Error('Envoi annulé');
        const donnees = new Uint8Array(await fichier.slice(pos, pos + CHUNK).arrayBuffer());
        const trame = new Uint8Array(5 + donnees.byteLength);
        trame[0] = FRAME_UPLOAD;
        new DataView(trame.buffer).setUint32(1, id);
        trame.set(donnees, 5);
        this.canal().envoyerBinaire(trame.buffer);
        obs.progres?.(id, Math.min(fichier.size, pos + donnees.byteLength));
      }
      await this.requete(id, { t: 'fin_televersement' });
      obs.fini?.(id, fichier.size);
    } catch (err) {
      this.attentes.delete(id);
      obs.erreur?.(id, err.message);
      throw err;
    }
  }
}
